import { DEFAULT_LOCALE, isSupportedLocale, type SiteLocale } from '@/lib/i18n/locales';
import { hasLocaleTranslation } from '@/lib/i18n/translated-slugs';

function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed) return '/';

  const [withoutQuery] = trimmed.split(/[?#]/);
  const withLeadingSlash = withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
  const withoutTrailingSlash =
    withLeadingSlash.length > 1 ? withLeadingSlash.replace(/\/+$/, '') : withLeadingSlash;

  return withoutTrailingSlash || '/';
}

export function getLocaleFromPathname(pathname: string): SiteLocale {
  const normalized = normalizePathname(pathname);
  const [firstSegment] = normalized.split('/').filter(Boolean);

  if (firstSegment && isSupportedLocale(firstSegment) && firstSegment !== DEFAULT_LOCALE) {
    return firstSegment;
  }

  return DEFAULT_LOCALE;
}

export function stripLocalePrefix(pathname: string): string {
  const normalized = normalizePathname(pathname);
  const segments = normalized.split('/').filter(Boolean);
  const firstSegment = segments[0];

  if (!firstSegment || !isSupportedLocale(firstSegment) || firstSegment === DEFAULT_LOCALE) {
    return normalized;
  }

  if (segments.length === 1) return '/';
  if (normalized.startsWith(`/${firstSegment}/`)) {
    const withoutPrefix = normalized.replace(new RegExp(`^/${firstSegment}`), '');
    return withoutPrefix || '/';
  }

  return normalized;
}

function isSingleSlugPath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  return segments.length === 1;
}

function hasLocalizedRoute(locale: SiteLocale, slug: string): boolean {
  if (locale === DEFAULT_LOCALE) return true;
  if (slug === 'blog') return true;
  if (locale !== 'en' && hasLocaleTranslation('en', slug)) return true;
  return hasLocaleTranslation(locale, slug);
}

function withLocalePrefix(pathname: string, locale: SiteLocale): string {
  const normalized = normalizePathname(pathname);
  if (locale === DEFAULT_LOCALE) {
    return normalized;
  }

  if (normalized === '/') {
    return `/${locale}`;
  }

  return `/${locale}${normalized}`;
}

export function localizeInternalHref(href: string, locale: SiteLocale): string {
  const normalizedHref = normalizePathname(href);
  if (locale !== DEFAULT_LOCALE) {
    const expectedPrefix = `/${locale}`;
    if (normalizedHref === expectedPrefix || normalizedHref.startsWith(`${expectedPrefix}/`)) {
      return normalizedHref;
    }
  }

  const stripped = stripLocalePrefix(href);
  if (locale === DEFAULT_LOCALE) {
    return stripped;
  }

  if (stripped === '/') {
    return `/${locale}`;
  }

  if (isSingleSlugPath(stripped)) {
    const slug = stripped.replace(/^\/+/, '');
    if (hasLocalizedRoute(locale, slug)) {
      return withLocalePrefix(stripped, locale);
    }
  }

  return stripped;
}

export function getLanguageSwitchPath(pathname: string, targetLocale: SiteLocale): string {
  const stripped = stripLocalePrefix(pathname);

  if (stripped === '/') {
    return withLocalePrefix('/', targetLocale);
  }

  if (isSingleSlugPath(stripped)) {
    const slug = stripped.replace(/^\/+/, '');
    if (hasLocalizedRoute(targetLocale, slug)) {
      return withLocalePrefix(stripped, targetLocale);
    }
  }

  return withLocalePrefix('/', targetLocale);
}
